/* eslint-disable max-len */
import { ReactNode, useEffect, useState } from 'react';
import { useStore } from 'react-redux';
import AppLoader, { AppLoaderVariant } from 'core/common/components/AppLoader';
import { useIsomorphicLayoutEffect, useRouter, useServices } from 'core/common/hooks';
import { RootInitialState } from 'core/common/store';
import { cloneDeep } from 'core/common/utils/cloneDeep';
import { useReportWebVitals } from 'core/common/web-vitals/hooks';
import { funnelFeatureDefaultValue } from 'core/feature-flags/config/funnelFeature';
import { FeatureFlagsConfig } from 'core/feature-flags/entities';
import { useFeatureFlags } from 'core/feature-flags/hooks';
import { Flow } from 'core/flow/entities';
import { FunnelConfig } from 'core/funnel/entities';
import i18n from 'core/i18n';
import { useLanguage } from 'core/localization/hooks';
import { useAnalyticsIds, useInitBingAds } from 'core/scripts/hooks';
import { getConditionAttributes } from '../store';
import { FunnelContext } from './FunnelContext';

type FunnelProviderDeprecatedProps = {
  children: ReactNode;
  resources: FunnelConfig;
  features: FeatureFlagsConfig['features'];
};
/** @deprecated. Use FunnelProvider instead **/
export const FunnelProviderDeprecated = ({
  children,
  resources,
  features,
}: FunnelProviderDeprecatedProps) => {
  const [pathname, setPathname] = useState('');
  const [featuresUpdated, setFeaturesUpdated] = useState(false);
  const { locale = 'en' } = useRouter();

  useEffect(() => {
    setPathname(window.location.pathname);
  }, []);

  const featureFlags = useFeatureFlags();

  const { analyticsService, loggingService, funnelSettingsService } = useServices();

  const store = useStore<RootInitialState>();

  const { language } = useLanguage();

  const { flows, analytics, ...quizWithoutFlows } = resources;

  const funnel = featureFlags.getFeatureValue(resources.name, funnelFeatureDefaultValue);

  const flowId = funnel.flow;

  const activeFlow: Flow | undefined = flows.find(({ name }) => name === flowId);
  const foundedDefaultFlow: Flow | undefined = flows.find(({ name }) => name === 'default_flow');

  const defaultFlow: Flow = foundedDefaultFlow ?? flows[0];

  const funnelConditions = getConditionAttributes(store.getState());

  const analyticsPixelIds = useAnalyticsIds({
    funnelAnalytics: analytics,
    funnelConditions,
  });

  useReportWebVitals();
  useInitBingAds(analyticsPixelIds.bingAds.combined);

  useIsomorphicLayoutEffect(() => {
    analyticsService.addUserAssociatedId(featureFlags.getSplitId());
    analyticsService.funnelOpened({ funnelName: resources.name, funnelLocale: locale });
    funnelSettingsService.setFunnelName(resources.name);
    funnelSettingsService.setFunnelLocale(locale);
  }, [analyticsService, funnelSettingsService, resources.name, locale]);

  useIsomorphicLayoutEffect(() => {
    analyticsService.trackUserLanguage(language);
  }, [analyticsService, language]);

  useIsomorphicLayoutEffect(() => {
    if (!activeFlow && defaultFlow) {
      loggingService.warn(
        `Flow is missing. "${defaultFlow.name}" was used instead. Be sure to declare the neccessary flow in "quizResources" of the "${resources.name}" funnel.`,
      );
      analyticsService.flowOpened(defaultFlow.name);
    }

    if (activeFlow) {
      analyticsService.flowOpened(activeFlow.name);
    }
  }, [activeFlow]);

  if (!activeFlow && defaultFlow.translations) {
    i18n.addResourceBundle(
      defaultFlow.language,
      'translation',
      // in cause when defaultFlow.translations object stored in redux
      // all objects have been frozen by immer https://immerjs.github.io/immer/freezing/
      // addResourceBundle method can't work properly with frozen object
      cloneDeep(defaultFlow.translations),
      true,
      true,
    );
  }

  /**
   * @deprecated Use funnel instead of this
   */
  if (!featureFlags.getAttributes().quizName) {
    featureFlags.setAttributes({ quizName: resources.name });
  }

  if (!featureFlags.getAttributes().funnel) {
    featureFlags.setAttributes({ funnel: resources.name });
  }

  if (features && !featuresUpdated) {
    // TODO: implement logic to avoid using of "setFeatures" method
    featureFlags.setFeatures(features);
    setFeaturesUpdated(true);

    return null;
  }

  const isDraftLink = pathname.startsWith('/draft/');
  const showingAppLoader = !isDraftLink && !featuresUpdated;
  const appLoaderVariant: AppLoaderVariant = 'filled';

  if (!activeFlow || !features) {
    return (
      <FunnelContext.Provider
        value={{
          ...quizWithoutFlows,
          flow: defaultFlow,
          analyticsIds: {
            facebook: analyticsPixelIds.facebook.combined,
            tikTok: analyticsPixelIds.tikTok.combined,
          },
        }}
      >
        {showingAppLoader && <AppLoader variant={appLoaderVariant} />}
        {children}
      </FunnelContext.Provider>
    );
  }

  if (activeFlow && activeFlow.translations) {
    i18n.addResourceBundle(
      activeFlow.language,
      'translation',
      // in cause when activeFlow.translations object stored in redux
      // all objects have been frozen by immer https://immerjs.github.io/immer/freezing/
      // addResourceBundle method can't work properly with frozen object
      cloneDeep(activeFlow.translations),
      true,
      true,
    );
  }

  return (
    <FunnelContext.Provider
      value={{
        ...quizWithoutFlows,
        flow: activeFlow,
        analyticsIds: {
          facebook: analyticsPixelIds.facebook.combined,
          tikTok: analyticsPixelIds.tikTok.combined,
        },
      }}
    >
      {showingAppLoader && <AppLoader variant={appLoaderVariant} />}
      {children}
    </FunnelContext.Provider>
  );
};
